import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  padding: 20px;
`;

const LoginCard = styled.div`
  display: flex;
  width: 100%;
  max-width: 860px;
  min-height: 500px;
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: var(--shadow-heavy);

  @media (max-width: 768px) {
    flex-direction: column;
    min-height: 0;
    max-width: 420px;
  }
`;

// Turquoise brand panel (the "rectangle"), connected to the form by a gray seam
const BrandPanel = styled.div`
  flex: 0 0 42%;
  background: var(--primary-color);
  color: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 48px 32px;
  text-align: center;
  border-left: 4px solid var(--secondary-color);

  @media (max-width: 768px) {
    flex: none;
    padding: 32px 24px;
    border-left: none;
    border-bottom: 4px solid var(--secondary-color);
  }
`;

const BrandTitle = styled.h1`
  font-size: 30px;
  font-weight: 700;
  margin: 0;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const BrandDivider = styled.span`
  width: 48px;
  height: 3px;
  background: #ffffff;
  border-radius: 2px;
  opacity: 0.85;
`;

const BrandSubtitle = styled.p`
  font-size: 15px;
  line-height: 1.6;
  margin: 0;
  opacity: 0.92;
  max-width: 240px;
`;

const FormPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 48px;

  @media (max-width: 768px) {
    padding: 32px 24px;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 28px;
`;

const LogoImage = styled.img`
  height: 72px;
  width: auto;
  max-width: 100%;
  object-fit: contain;

  @media (max-width: 768px) {
    height: 58px;
  }
`;


const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  text-align: right;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: var(--text-primary);
  font-size: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 16px;
  direction: rtl;
  text-align: right;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(0, 142, 162, 0.1);
  }

  &.error {
    border-color: var(--error-color);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 12px 16px;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background-color: var(--primary-hover);
  }

  &:disabled {
    background-color: var(--text-tertiary);
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background-color: var(--error-light);
  color: var(--error-color);
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  text-align: right;
  border: 1px solid var(--error-color);
`;

const LinkContainer = styled.div`
  margin-top: 20px;
  text-align: center;
`;

const StyledLink = styled(Link)`
  color: var(--primary-color);
  text-decoration: none;
  font-size: 14px;

  &:hover {
    text-decoration: underline;
  }
`;

const LoginPage: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check for returnUrl in query params (from 401 redirect) or location state (from ProtectedRoute)
  const searchParams = new URLSearchParams(location.search);
  const returnUrl = searchParams.get('returnUrl');
  const from = returnUrl || location.state?.from?.pathname || '/dashboard';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email.trim()) {
      newErrors.email = 'דוא"ל הוא שדה חובה';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'דוא"ל לא תקין';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'סיסמה היא שדה חובה';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const result = await login({
        email: formData.email,
        password: formData.password
      });

      if (result.isSuccess) {
        navigate(from, { replace: true });
      } else {
        setErrorMessage(result.message || 'שגיאה בהתחברות');
      }
    } catch (error) {
      setErrorMessage('שגיאה בהתחברות');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        <BrandPanel>
          <BrandTitle>ברוכים הבאים</BrandTitle>
          <BrandDivider />
          <BrandSubtitle>הזינו את פרטי ההתחברות שלכם כדי להמשיך למערכת</BrandSubtitle>
        </BrandPanel>

        <FormPanel>
          <LogoContainer>
            <LogoImage
              src="/logomagialecha.png"
              alt="לוגו מגיע לך"
              loading="eager"
            />
          </LogoContainer>

          {errorMessage && (
            <ErrorMessage>{errorMessage}</ErrorMessage>
          )}

          <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="email">דוא"ל</Label>
            <Input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={errors.email ? 'error' : ''}
              disabled={isLoading}
              autoComplete="username"
            />
            {errors.email && (
              <ErrorMessage>{errors.email}</ErrorMessage>
            )}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="password">סיסמה</Label>
            <Input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={errors.password ? 'error' : ''}
              disabled={isLoading}
              autoComplete="current-password"
            />
            {errors.password && (
              <ErrorMessage>{errors.password}</ErrorMessage>
            )}
          </FormGroup>

          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'מתחבר...' : 'כניסה'}
          </Button>
          </Form>

          <LinkContainer>
            <StyledLink to="/forgot-password">
              שכחת סיסמה?
            </StyledLink>
          </LinkContainer>
        </FormPanel>
      </LoginCard>
    </LoginContainer>
  );
};

export default LoginPage;